/**
 * Dados mocados do modulo Grupos.
 *
 * Grupos sao conversas informais montadas a partir de pessoas — o protótipo
 * separa `kind: 'Grupo'` dos canais (`docs/design-slices/_shell.html:2753`) e
 * cria grupo por um fluxo de dois passos (`_shell.html:88-158`): primeiro os
 * participantes, depois nome e foto.
 *
 * Mesma referencia temporal de `mocks/chat.ts`: 6 de agosto de 2026.
 */
import type { ChatChannel, ChatMessage, Presence } from './chat.ts'

/** Pessoa disponivel para entrar em um grupo (passo 1 do fluxo). */
export type GroupContact = {
  id: string
  name: string
  initials: string
  area: string
  /** Frase curta mostrada abaixo do nome na lista de selecao. */
  note: string
  presence: Presence
}

export const MOCK_CONTACTS: readonly GroupContact[] = [
  {
    id: 'ct-mariana',
    name: 'Mariana Bezerra',
    initials: 'MB',
    area: 'Produto',
    note: 'Produto · revisando o escopo da virada',
    presence: 'online',
  },
  {
    id: 'ct-rafael',
    name: 'Rafael Andrade',
    initials: 'RA',
    area: 'Operações',
    note: 'Operações · roteiro de rollback',
    presence: 'ausente',
  },
  {
    id: 'ct-juliana',
    name: 'Juliana Prado',
    initials: 'JP',
    area: 'Atendimento',
    note: 'Atendimento · conta Brasília Shopping',
    presence: 'online',
  },
  {
    id: 'ct-thiago',
    name: 'Thiago Nogueira',
    initials: 'TN',
    area: 'Engenharia',
    note: 'Engenharia · integração de pagamentos',
    presence: 'ocupado',
  },
  {
    id: 'ct-camila',
    name: 'Camila Duarte',
    initials: 'CD',
    area: 'Engenharia',
    note: 'Engenharia · catálogos e cache',
    presence: 'online',
  },
  {
    id: 'ct-bruno',
    name: 'Bruno Teixeira',
    initials: 'BT',
    area: 'Operações',
    note: 'Operações · SLA dos contratos',
    presence: 'offline',
  },
  {
    id: 'ct-paula',
    name: 'Paula Andrade',
    initials: 'PA',
    area: 'Marketing',
    note: 'Marketing · calendário de campanhas',
    presence: 'ausente',
  },
  {
    id: 'ct-tiago',
    name: 'Tiago Moreira',
    initials: 'TM',
    area: 'Atendimento',
    note: 'Atendimento · fila de chamados',
    presence: 'offline',
  },
]

/** Grupos usam o mesmo formato de conversa dos canais, com icone proprio. */
export const MOCK_GROUPS: readonly ChatChannel[] = [
  {
    id: 'gr-squad-orbind',
    name: 'Squad Orbind',
    kind: 'time',
    description: 'Grupo do time que toca a plataforma no dia a dia.',
    icon: 'fas fa-users',
    members: ['Camila Duarte', 'Thiago Nogueira', 'Mariana Bezerra', 'Cristian Ramos'],
    memberCount: 4,
    unread: 2,
    mentionsMe: true,
    lastMessagePreview: 'Thiago Nogueira: Deixo a homologação pronta assim que os testes passarem.',
    lastMessageAt: '08:30',
    presence: null,
    liveMeetingId: null,
    contextCode: null,
    contextLabel: null,
    readOnly: false,
  },
  {
    id: 'gr-virada-jk',
    name: 'Virada JK — plantão',
    kind: 'projeto',
    description: 'Grupo temporário para o plantão da virada de ambiente.',
    icon: 'fas fa-users',
    members: ['Rafael Andrade', 'Juliana Prado', 'Bruno Teixeira', 'Cristian Ramos'],
    memberCount: 4,
    unread: 1,
    mentionsMe: false,
    lastMessagePreview: 'Bruno Teixeira: Fico de sobreaviso das 22h às 02h.',
    lastMessageAt: '07:41',
    presence: null,
    liveMeetingId: null,
    contextCode: 'DMD-0047',
    contextLabel: 'DMD-0047 · Virada de ambiente JK Iguatemi',
    readOnly: false,
  },
  {
    id: 'gr-cafe-produto',
    name: 'Café de produto',
    kind: 'produto',
    description: 'Conversa informal sobre ideias de produto.',
    icon: 'fas fa-users',
    members: ['Mariana Bezerra', 'Paula Andrade', 'Cristian Ramos'],
    memberCount: 3,
    unread: 0,
    mentionsMe: false,
    lastMessagePreview: 'Você: Anotei as duas ideias no documento de descoberta.',
    lastMessageAt: 'Ontem',
    presence: null,
    liveMeetingId: null,
    contextCode: null,
    contextLabel: null,
    readOnly: false,
  },
]

export const MOCK_GROUP_MESSAGES: Record<string, readonly ChatMessage[]> = {
  'gr-squad-orbind': [
    { id: 'g-so-01', kind: 'date', text: 'Hoje' },
    {
      id: 'g-so-02',
      kind: 'message',
      author: 'Camila Duarte',
      initials: 'CD',
      text: 'Bom dia! Priorizei a correção do cadastro de CNPJ para hoje cedo.',
      time: '08:12',
      mine: false,
      read: true,
    },
    {
      id: 'g-so-03',
      kind: 'message',
      author: 'Cristian Ramos',
      initials: 'CR',
      text: 'Perfeito. Deixo a revisão do critério de aceite para depois do almoço.',
      time: '08:18',
      mine: true,
      read: true,
    },
    {
      id: 'g-so-04',
      kind: 'message',
      author: 'Thiago Nogueira',
      initials: 'TN',
      text: '@Cristian consegue olhar o PR da árvore recursiva ainda hoje?',
      time: '08:27',
      mine: false,
      read: false,
    },
    {
      id: 'g-so-05',
      kind: 'message',
      author: 'Thiago Nogueira',
      initials: 'TN',
      text: 'Deixo a homologação pronta assim que os testes passarem.',
      time: '08:30',
      mine: false,
      read: false,
    },
  ],
  'gr-virada-jk': [
    { id: 'g-vj-01', kind: 'date', text: 'Hoje' },
    {
      id: 'g-vj-02',
      kind: 'system',
      text: 'Você criou o grupo “Virada JK — plantão” com 3 participantes',
      contextCode: null,
    },
    {
      id: 'g-vj-03',
      kind: 'message',
      author: 'Bruno Teixeira',
      initials: 'BT',
      text: 'Fico de sobreaviso das 22h às 02h.',
      time: '07:41',
      mine: false,
      read: false,
    },
  ],
  'gr-cafe-produto': [
    { id: 'g-cp-01', kind: 'date', text: 'Ontem' },
    {
      id: 'g-cp-02',
      kind: 'message',
      author: 'Paula Andrade',
      initials: 'PA',
      text: 'Ideia: mostrar o tempo médio de resposta do canal direto no perfil da pessoa.',
      time: '15:02',
      mine: false,
      read: true,
    },
    {
      id: 'g-cp-03',
      kind: 'message',
      author: 'Cristian Ramos',
      initials: 'CR',
      text: 'Anotei as duas ideias no documento de descoberta.',
      time: '15:20',
      mine: true,
      read: true,
    },
  ],
}
